import React, { useState, useEffect, useRef } from 'react';
import { STUDIO } from '../data/studio';
import { ArrowUpRight, Scissors, Palette, Gift, Frame, Sparkles, Shirt } from 'lucide-react';

/**
 * Three-panel strip below the hero — the one structure worth keeping from the
 * TerraElix reference, retuned to the warm palette and to a commission studio
 * rather than a shop. No figures anywhere: this atelier quotes per piece.
 */

const CRAFTS = [
  { Icon: Scissors, ring: 'bg-[#4E2C23]', text: 'Aari and Zardosi embroidery, worked by hand on raw silk' },
  { Icon: Palette, ring: 'bg-[#8C3B1E]', text: 'Resin, canvas and fine art finished in the studio' },
  { Icon: Shirt, ring: 'bg-[#6A3E33]', text: 'Bespoke designing and master tailoring, cut to your measure' },
  { Icon: Gift, ring: 'bg-[#C75841]', text: 'Wedding and festive hampers, assembled piece by piece' },
  { Icon: Frame, ring: 'bg-[#8B4A28]', text: 'Heritage 3D frames that preserve what you want to keep' },
];

const ROTATE_MS = 3800;

export default function AtelierStrip({ onOpenQuote, onExplore }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    // An unpausable rotation is an accessibility failure, and a rotation that
    // ignores reduced-motion is a worse one.
    if (paused || reduce.matches) return undefined;

    timer.current = setInterval(() => {
      setActive((i) => (i + 1) % CRAFTS.length);
    }, ROTATE_MS);
    return () => clearInterval(timer.current);
  }, [paused]);

  return (
    <section
      aria-label="What the studio makes"
      className="relative z-10 grid grid-cols-1 md:grid-cols-[2fr_1fr_2fr] border-y border-[#4E2C23]/10"
    >
      {/* ---------- Panel 1: the invitation ---------- */}
      <div className="relative bg-[#ECEDEC] overflow-hidden px-6 py-10 sm:px-9 sm:py-12 flex flex-col justify-between gap-6 min-h-[220px]">
        <h2 className="font-90s font-black text-[#4E2C23] text-2xl sm:text-[28px] lg:text-[34px] leading-[1.08] tracking-tight max-w-[350px] text-balance">
          Every order starts with a conversation, not a catalogue
        </h2>

        <button
          onClick={onOpenQuote}
          className="group self-start inline-flex items-center gap-2 text-[#4E2C23] text-base lg:text-lg font-medium underline underline-offset-4 decoration-[#4E2C23]/30 hover:decoration-[#E2725B] hover:text-[#C75841] transition-colors"
        >
          Start an order
          <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>

        {/* Warm bleed so the flat panel does not read as a grey box */}
        <div
          className="absolute -right-16 -bottom-16 w-56 h-56 rounded-full pointer-events-none opacity-60"
          style={{ background: 'radial-gradient(circle, rgba(255,218,185,.9) 0%, transparent 70%)' }}
        />
      </div>

      {/* ---------- Panel 2: rotating disciplines ---------- */}
      <div
        className="relative bg-[#FEFDF9] px-6 py-10 sm:px-7 sm:py-12 flex flex-col justify-between gap-6 min-h-[220px] border-t md:border-t-0 md:border-x border-[#4E2C23]/10"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
      >
        <div className="relative flex-1" aria-live="polite">
          {CRAFTS.map((craft, i) => {
            const { Icon } = craft;
            return (
              <div
                key={craft.text}
                aria-hidden={i !== active}
                className={`flex items-start gap-3.5 transition-all duration-500 ease-atelier ${
                  i === active
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4 absolute inset-0 pointer-events-none'
                }`}
              >
                <span
                  className={`${craft.ring} w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0`}
                >
                  <Icon className="w-5 h-5 text-[#FFF7F0]" strokeWidth={1.7} />
                </span>
                <p className="text-sm sm:text-base lg:text-[17px] text-[#4E2C23]/85 leading-[1.35] tracking-tight">
                  {craft.text}
                </p>
              </div>
            );
          })}
        </div>

        <div className="flex gap-1.5" role="tablist" aria-label="Craft disciplines">
          {CRAFTS.map((craft, i) => (
            <button
              key={craft.text}
              role="tab"
              aria-selected={i === active}
              aria-label={craft.text}
              onClick={() => setActive(i)}
              className="flex-1 h-3 flex items-center group"
            >
              <span
                className={`block w-full h-0.5 rounded-full transition-colors duration-300 ${
                  i === active ? 'bg-[#4E2C23]' : 'bg-[#4E2C23]/20 group-hover:bg-[#E2725B]/60'
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      {/* ---------- Panel 3: the proof ---------- */}
      <div className="relative bg-[#4E2C23] px-6 py-10 sm:px-9 sm:py-12 flex items-center gap-6 min-h-[220px] border-t md:border-t-0 border-[#4E2C23]/10 overflow-hidden">
        <div
          className="absolute -left-10 -top-10 w-48 h-48 rounded-full pointer-events-none opacity-25"
          style={{ background: 'radial-gradient(circle, #E2725B 0%, transparent 70%)' }}
        />

        <img
          src="/logo-mark-256.webp"
          alt=""
          aria-hidden="true"
          loading="lazy"
          width="256"
          height="256"
          className="relative w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 object-contain flex-shrink-0 opacity-90"
        />

        <div className="relative space-y-1.5">
          {/* Was "850+" pieces — a volume figure nobody counted. The founding
              year is the one number here that a customer can check. */}
          <div className="text-[#FFF7F0] font-90s font-black text-3xl sm:text-4xl lg:text-[40px] leading-none tracking-tight tabular-nums">
            {STUDIO.since ? `Since ${STUDIO.since}` : 'Made to order'}
          </div>
          <p className="text-[#FFDAB9]/70 text-sm sm:text-base leading-[1.3] max-w-[240px]">
            Every piece made by hand to order, each quoted for the work it actually takes.
          </p>
          <button
            onClick={onExplore}
            className="group inline-flex items-center gap-1.5 text-[#FFDAB9] text-sm font-semibold pt-1 hover:text-white transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.8} />
            See the work
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </section>
  );
}
